import { useEffect, useState } from "react";

import { Card, CardContent, useMediaQuery } from "@material-ui/core";

import { usePromiseTracker, trackPromise } from 'react-promise-tracker';

import LoadingProgress from "../../LoadingProgress";
import ForbiddenPageCard from "../../ForbiddenPageCard";
import RequestFinancialAdjustmentModal from './request.financial.adjustment.modal.component';
import FinancialSectionHeader from './financial.section.header.component';
import FinancialCustomerSection from './financial.customer.section.component';
import FinancialFaciliterSection from './financial.faciliter.section.component';
import FinancialCardActions from './financial.card.actions.component';
import FinancialCompositionToggle from './financial.composition.toggle.component';
import { MARGIN_EXCEPTION_ROLES, hasAnyRole } from '../../../domain/constant/financial.authorization.constant';
import RequestFinancialRemoveDiscountModal from "./request.financial.remove.discount.modal.component";


import loginService from "../../../domain/services/login.service";
import requestService from "../../../domain/services/request/request.service";

const DEFAULT_ROLE = 'operator';

const SECTION_INCOME_COLOR = '#1f8f5f';

const SECTION_EXPENSE_COLOR = '#b8651b';

const INCOME_SECTION_ROLES = ['admin', 'financial'];

const EXPENSE_SECTION_ROLES = ['admin', 'operator', 'financial'];

const FACILITER_PRICE_TYPES = ['faciliter', 'faciliterReviewer'];

const SECTION_SURFACE_CLASS = 'rounded-xl bg-[#f7f7f8] px-6 py-5';

const NARROW_SCREEN = '(max-width:600px)';

const CARD_PADDING_NARROW = 24;

const CARD_PADDING_WIDE = 40;

const cardPaddingOf = (isNarrow) => ({ padding: isNarrow ? CARD_PADDING_NARROW : CARD_PADDING_WIDE });

const isSomeoneElse = (price, userId) => String(price.responsibleUserId) !== String(userId);

const userRoles = () => loginService.getUserAuthentication()?.roles || [];

const currentUserRole = () => MARGIN_EXCEPTION_ROLES
    .find(role => loginService.hasPermissionRole(role, loginService.getUserAuthentication())) || DEFAULT_ROLE;

const customerAmountOfService = (pricesByService, serviceId) => pricesByService
    .filter(price => price.type === 'customer' && String(price.serviceId) === String(serviceId))
    .reduce((total, price) => total + Number(price.amount || 0), 0);

function RequestFinancial({ request, mainRequestId, setAlert, isSmall }) {

    const { promiseInProgress } = usePromiseTracker();

    const [pricesByService, setPricesByService] = useState([]);

    const [removeDiscountModal, setRemoveDiscountModal] = useState({});
    const [adjustmentSheet, setAdjustmentSheet] = useState({});
    const [isDetailed, setIsDetailed] = useState(false);
    const isNarrow = useMediaQuery(NARROW_SCREEN);

    useEffect(() => findPricesByService(mainRequestId), []);

    const findPricesByService = (mainId) =>
        trackPromise(
            requestService
                .findPricesByMainId(mainId)
                .then(setPricesByService)
                .catch(() => setAlert('Não foi possível encontrar os preços dos faciliters.', 'error'))
        );


    const openAdjustmentSheet = (price) => setAdjustmentSheet({
        isOpen: true,
        priceInfo: {
            requestId: price.requestId,
            responsibleId: price.responsibleId,
            responsibleName: price.responsibleName,
            serviceName: price.serviceName,
            label: price.label,
            amount: price.amount,
            customerAmount: customerAmountOfService(pricesByService, price.serviceId),
            priceFees: price.priceFees || []
        }
    });

    const faciliterLinks = pricesByService.filter(price =>
        FACILITER_PRICE_TYPES.includes(price.type)
        && isSomeoneElse(price, loginService.getUserAuthentication()?.id));

    const editableLinks = faciliterLinks.filter(price => !!price.serviceId && !price.isUnlinked);

    const closeAdjustmentSheet = () => setAdjustmentSheet(current => ({ ...current, isOpen: false }));

    const openAdjustmentSheetForFirstLink = () => editableLinks.length && openAdjustmentSheet(editableLinks[0]);

    const openRemoveDiscountModal = (price) =>
        setRemoveDiscountModal({
            isOpen: true,
            data: {
                requestId: price.requestId,
                responsibleId: price.responsibleId,
                responsibleName: price.responsibleName,
                serviceName: price.serviceName
            }
        });

    const groupCustomerPrices = (priceList) => {
        const groupedPrices = priceList.reduce((list = [], price) => {
            const groupIndex = list.findIndex(group =>
                group.serviceId === price.serviceId &&
                group.packageId === price.packageId &&
                group.label === price.label
            );

            if (groupIndex === -1) {
                return [
                    ...list,
                    {
                        serviceId: price.serviceId,
                        packageId: price.packageId,
                        label: price.label,
                        individualPrice: price.amount,
                        amount: price.amount,
                        billingIds: [price.billingId],
                        packageDescription: price.packageDescription,
                        serviceName: price.serviceName,
                    }
                ]
            }

            list[groupIndex].amount += price.amount;
            list[groupIndex].billingIds.push(price.billingId);

            return list;

        }, []);

        const groupedByPackage = groupedPrices.reduce((list = [], price) => {
            const groupIndex = list.findIndex(group => group.packageId === price.packageId);

            if (groupIndex === -1) {
                return [
                    ...list,
                    {
                        packageId: price.packageId,
                        packageDescription: price.packageDescription,
                        services: [price],
                    }
                ];
            }

            list[groupIndex].services.push(price);

            return list;
        }, []);

        return groupedByPackage;
    }

    const getCustomerPrices = () => {

        const prices = pricesByService.filter(price => price.type == 'customer');

        if (prices.length == 0)
            return <p className="m-0 text-xs text-gray-500">Não há débitos nesta solicitação.</p>;

        return <FinancialCustomerSection groups={groupCustomerPrices(prices)} prices={prices} />;
    }

    const getFacilitersPricesByService = () => {

        if (!faciliterLinks.length)
            return <p className="m-0 text-xs text-gray-500">Não há faciliters vinculados aos serviços.</p>;

        return (
            <FinancialFaciliterSection
                links={faciliterLinks}
                isDetailed={isDetailed}
                onRemoveDiscount={openRemoveDiscountModal}
            />
        );
    }

    const financialSections = [
        {
            label: 'Entradas',
            description: 'Valores pagos pelo cliente',
            captionColor: SECTION_INCOME_COLOR,
            validator: () => hasAnyRole(userRoles(), INCOME_SECTION_ROLES),
            component: () => getCustomerPrices()
        },
        {
            label: 'Saídas',
            description: 'Valores pagos aos faciliters',
            captionColor: SECTION_EXPENSE_COLOR,
            action: faciliterLinks.length
                ? <FinancialCompositionToggle isDetailed={isDetailed} onToggle={() => setIsDetailed(!isDetailed)} />
                : null,
            validator: () => hasAnyRole(userRoles(), EXPENSE_SECTION_ROLES),
            component: () => getFacilitersPricesByService()
        }
    ]

    const visibleSections = financialSections.filter(section => !section.validator || section.validator());

    const checkIsFaciliterReviewer = () => {
        const user = loginService.getUserAuthentication();
        return (request.faciliterReviewers || [])[0]?.userId == user.id;
    }

    if (promiseInProgress || !request.id)
        return (
            <LoadingProgress />
        )

    if (!loginService.isInternal() && !checkIsFaciliterReviewer())
        return (
            <ForbiddenPageCard />
        )

    return (
        <>

            <Card style={{ width: '100%' }}>
                <CardContent style={cardPaddingOf(isNarrow)}>

                    <header className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <h2 className="m-0 text-base font-semibold text-[#1d1d1f]">Valores do pedido</h2>

                        <FinancialCardActions editableLinks={editableLinks} onEdit={openAdjustmentSheetForFirstLink} />
                    </header>

                    {visibleSections.map((section, index) => (
                        <section key={section.label} className={index ? 'mt-6' : ''}>
                            <FinancialSectionHeader section={section} />

                            <div className={SECTION_SURFACE_CLASS}>{section.component()}</div>
                        </section>
                    ))}

                </CardContent>
            </Card>

            <RequestFinancialAdjustmentModal
                isOpen={!!adjustmentSheet.isOpen}
                links={editableLinks}
                priceInfo={adjustmentSheet.priceInfo || {}}
                role={currentUserRole()}
                onSelect={openAdjustmentSheet}
                onClose={closeAdjustmentSheet}
                onApplied={() => findPricesByService(mainRequestId)}
                setAlert={setAlert}
            />

            <RequestFinancialRemoveDiscountModal
                isOpen={removeDiscountModal.isOpen}
                faciliterServiceInfo={removeDiscountModal.data || {}}
                refresh={() => findPricesByService(mainRequestId)}
                handleClose={() => setRemoveDiscountModal({ ...removeDiscountModal, isOpen: false })}
                setAlert={setAlert}
            />

        </>
    );
}

export default RequestFinancial;
